import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ChatEntryPoint } from '@atlaskit/rovo-triggers/chat-entry-point';
import { clearSelectedAgentDraftOverride } from '@atlassian/conversation-assistant-chat-prompt-input/controllers/selected-agent-drafts/clearSelectedAgentDraftOverride';
import {
	type ConversationChannelScope,
	type LastAgentMessageStatus,
	type MessageType,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	type AgentMetadata,
	type AgentVersion,
	CHAT_STORE_STATUS,
	type ConversationResultAttributionContext,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { createSafeAction, type SafeAction } from './createSafeAction';
import { initializeConversationData } from './initializeConversationData';
import { resolveConversationSeedAgent } from './resolveConversationSeedAgent';
import { setSelectedConversation } from './setSelectedConversation';
import type { ChatStoreAction } from './types';

type CreateConversationOptimisticallyOptions = {
	name?: string;
	sourceId?: string;
	id?: string;
	timeCreated?: string;
	lastMessageTimestamp?: string;
	sourceAgentId?: string;
	status?: CHAT_STORE_STATUS;
	lastMessageType?: MessageType[];
	lastAgentMessageStatus?: LastAgentMessageStatus;
	agent?: AgentMetadata;
	agentVersion?: AgentVersion;
	resultAttributionContext?: ConversationResultAttributionContext;
	rovoJourneyId?: string;
	/** Product surface that launched this conversation, used for analytics attribution. */
	chatEntryPoint?: ChatEntryPoint;
	isViewModeRemix?: boolean;
	rovoSpaceId?: string;
	scopes?: ConversationChannelScope[];
};

export const createConversationOptimistically: SafeAction<
	[CreateConversationOptimisticallyOptions]
> = createSafeAction(
	StoreActionLoggableName.createConversationOptimistically,
	(options: CreateConversationOptimisticallyOptions): ChatStoreAction =>
		async ({ dispatch }, { setMessages }) => {
			const conversationId = options?.id ?? EMPTY_CONVERSATION_ID;
			const time = new Date().toISOString();
			// Seed `conversation.agent` from the known optimistic conversation identity.
			const agentForOptimisticConversation = resolveConversationSeedAgent({
				backendAgent: options?.agent,
				agentId: options?.sourceAgentId,
				legacyAgent: options?.agent,
			});
			// Under the selected-agent gate the header derives the displayed agent
			// solely from `conversation.agent`. When this optimistic creation re-uses an
			// existing conversation slot (e.g. EMPTY_CONVERSATION_ID) the data is
			// shallow-merged, so omitting the `agent` key would leave a previously-seeded
			// specialist agent in place. Selecting "Chat with Rovo" (no agent id) must
			// therefore explicitly clear `conversation.agent` back to the default Rovo
			// agent rather than silently preserving the stale one.
			const optimisticConversationAgentData = agentForOptimisticConversation
				? { agent: agentForOptimisticConversation }
				: isExperimentEnabled('rovo_chat_mention_agents')
					? { agent: undefined } // explicit clear so "Chat with Rovo" resets a stale specialist
					: {};

			// A user-initiated top-selector choice ("Chat with Rovo" or another agent)
			// lands here, and it commonly re-initialises an existing conversation slot
			// (EMPTY_CONVERSATION_ID) rather than allocating a new id. The unsent
			// selected-agent draft is keyed by that same id, so without an explicit
			// reset a prior inline `@`-mention would survive the switch and keep
			// routing later prompts to the old agent. Any mention nodes already typed
			// stay in the prompt document and merely become inactive.
			if (isExperimentEnabled('rovo_chat_mention_agents')) {
				clearSelectedAgentDraftOverride(conversationId);
			}

			dispatch(
				initializeConversationData({
					conversationId,
					data: {
						id: conversationId,
						lastMessageTimestamp: options.lastMessageTimestamp ?? time,
						name: options.name ?? generateConversationName(),
						sourceAgentId: options.sourceAgentId,
						sourceId: options.sourceId ?? undefined,
						// Journey attribution belongs to a canonical conversation ID. Empty optimistic
						// slots are reused and must not retain attribution from a prior launch.
						rovoJourneyId: options.id ? options.rovoJourneyId : undefined,
						status: options?.status ?? CHAT_STORE_STATUS.idle,
						timeCreated: options?.timeCreated ?? time,
						traces: [],
						messages: [],
						lastMessageType: options?.lastMessageType ?? [],
						lastAgentMessageStatus: options?.lastAgentMessageStatus,
						resultAttributionContext: options?.resultAttributionContext,
						// Explicitly set rovoSpaceId (including undefined) so that re-initialising
						// EMPTY_CONVERSATION_ID without a space clears any stale rovoSpaceId left
						// from a previous space-scoped conversation.
						rovoSpaceId: options?.rovoSpaceId,
						// Same reasoning as rovoSpaceId: this action commonly re-initialises the
						// EMPTY_CONVERSATION_ID slot, and the data is shallow-merged, so the key
						// must be set even when undefined. Otherwise a chat launched from a
						// product surface would keep attributing later, unrelated chats to it.
						chatEntryPoint: options?.chatEntryPoint,
						isViewModeRemix: options?.isViewModeRemix,
						// Same reasoning again: a stale 3P cohort left on a re-initialised slot would
						// be mirrored onto an unrelated conversation's events, including the org-wide
						// AI-MAU events this experiment does not own (CCINT-8714).
						thirdPartyPrompt: undefined,
						...optimisticConversationAgentData,
						// Reusing the empty conversation must not retain another agent's version.
						...(fg('rovo_chat_reset_agent_version') || options?.agentVersion
							? { agentVersion: options?.agentVersion }
							: {}),
						...(options?.scopes ? { scopes: options.scopes } : {}),
					},
				}),
			);

			setMessages(conversationId, []);

			dispatch(
				setSelectedConversation({
					conversationId,
				}),
			);
		},
);
